/**
 * Embed Domain Allowlist — trusted sources for iframe embeds in content sections.
 *
 * Only domains listed here can be embedded via the IframeEmbed TipTap extension.
 * This prevents XSS and phishing by rejecting arbitrary iframe sources.
 *
 * The allowlist is loaded from the API (`/api/embed-allowlist`) at startup.
 * If the API is unreachable, the static FALLBACK_ALLOWLIST is used instead.
 * Call `hydrateEmbedAllowlist()` once at app init to prime from the API.
 */

import { fetchEmbedAllowlist } from '../../api/embedAllowlistApi';

export interface AllowedEmbedDomain {
  /** Hostname suffix to match (e.g., "ourworldindata.org" matches subdomains too). */
  domain: string;
  /** Human-readable label. */
  label: string;
  /** Default iframe height in px. */
  defaultHeight: number;
  /** Optional sandbox attribute. If omitted, a restrictive default is used. */
  sandbox?: string;
}

/** Default sandbox policy for iframes without a custom sandbox value. */
export const DEFAULT_SANDBOX = 'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox';

// ── Static fallback (used if API is unreachable) ─────────────────

const FALLBACK_ALLOWLIST: AllowedEmbedDomain[] = [
  { domain: 'ourworldindata.org', label: 'Our World in Data', defaultHeight: 600 },
  { domain: 'youtube.com', label: 'YouTube', defaultHeight: 400, sandbox: 'allow-scripts allow-same-origin allow-presentation' },
  { domain: 'youtu.be', label: 'YouTube', defaultHeight: 400, sandbox: 'allow-scripts allow-same-origin allow-presentation' },
  { domain: 'player.vimeo.com', label: 'Vimeo', defaultHeight: 400, sandbox: 'allow-scripts allow-same-origin allow-presentation' },
  { domain: 'public.tableau.com', label: 'Tableau Public', defaultHeight: 600, sandbox: 'none' },
  { domain: 'datastudio.google.com', label: 'Looker Studio', defaultHeight: 600 },
  { domain: 'lookerstudio.google.com', label: 'Looker Studio', defaultHeight: 600 },
  { domain: 'sdgs.un.org', label: 'UN SDG Tracker', defaultHeight: 600 },
  { domain: 'data.worldbank.org', label: 'World Bank Data', defaultHeight: 600 },
  { domain: 'databank.worldbank.org', label: 'World Bank DataBank', defaultHeight: 600 },
  { domain: 'globalforestwatch.org', label: 'Global Forest Watch', defaultHeight: 600, sandbox: 'none' },
  { domain: 'resourcewatch.org', label: 'Resource Watch', defaultHeight: 600, sandbox: 'none' },
  { domain: 'data-explorer.oecd.org', label: 'OECD Data Explorer', defaultHeight: 600 },
];

// ── Runtime allowlist (mutated by hydrate) ───────────────────────

let _allowlist: AllowedEmbedDomain[] = [...FALLBACK_ALLOWLIST];
let _hydrated = false;

/**
 * Fetch the allowlist from the API and replace the in-memory list.
 * Safe to call multiple times; only fetches once.
 * Called from app initialisation (e.g., App.tsx or auth bootstrap).
 */
export async function hydrateEmbedAllowlist(): Promise<void> {
  if (_hydrated) return;
  try {
    const apiDomains = await fetchEmbedAllowlist();
    if (apiDomains.length > 0) {
      _allowlist = apiDomains.map((d) => ({
        domain: d.domain,
        label: d.label,
        defaultHeight: d.defaultHeight,
        sandbox: d.sandbox || undefined,
      }));
    }
    _hydrated = true;
  } catch (err) {
    console.warn('[embedAllowlist] API fetch failed, using static fallback:', err);
    // Keep using FALLBACK_ALLOWLIST — already in _allowlist
  }
}

/** Current active allowlist (API-hydrated or static fallback). */
export function getEmbedAllowlist(): readonly AllowedEmbedDomain[] {
  return _allowlist;
}

/**
 * Check if a URL is on the embed allowlist.
 * Returns the matching domain config, or null if not allowed.
 */
export function matchAllowedDomain(url: string): AllowedEmbedDomain | null {
  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname.toLowerCase();
    return _allowlist.find((entry) =>
      hostname === entry.domain || hostname.endsWith('.' + entry.domain)
    ) || null;
  } catch {
    return null;
  }
}

/**
 * Get a display-friendly list of allowed domain labels (deduplicated).
 */
export function allowedDomainLabels(): string[] {
  const seen = new Set<string>();
  return _allowlist
    .filter((e) => {
      if (seen.has(e.label)) return false;
      seen.add(e.label);
      return true;
    })
    .map((e) => e.label);
}
